import { Link } from "react-router";
import { ArrowLeft, HardDrive } from "lucide-react";
import { THEME } from "@/config/theme";
import { listStoredCanvases, readCanvas } from "@/store/canvasPersistence";

/**
 * What this device is holding locally.
 *
 * Deliberately a readout, not a manager: clearing an entry that holds unsaved
 * edits would destroy work that exists nowhere else, so entries in that state
 * are flagged rather than made easy to delete.
 *
 * The number is per-browser, per-device — the same account on another machine
 * shows something different. That is why this lives here and not in Clerk's
 * account modal, which is account-scoped.
 */

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function StorageUsage() {
  const entries = listStoredCanvases().map((e) => {
    const saved = readCanvas(e.forgeId);
    return { ...e, unsaved: saved != null && saved.revision !== saved.syncedRevision };
  });
  const total = entries.reduce((sum, e) => sum + e.bytes, 0);

  return (
    <div
      className={`h-screen overflow-y-auto [color-scheme:dark] ${THEME.scrollbar.dark} ${THEME.panel.text} bg-canvas-bg`}
    >
      <div className="mx-auto max-w-3xl px-6 py-10 flex flex-col gap-8">
        <div className="flex flex-col gap-2">
          <Link
            to="/"
            className={`inline-flex items-center gap-1.5 ${THEME.mcpPage.hint} hover:text-zinc-200 w-fit transition-colors`}
          >
            <ArrowLeft size={14} />
            Back to canvas
          </Link>
          <h1 className={THEME.mcpPage.title}>Local storage</h1>
          <p className={THEME.mcpPage.intro}>
            Canvases this browser keeps on disk so they load instantly and survive
            a refresh. This is per-device — another machine signed in as you will
            show something different.
          </p>
        </div>

        <div className="flex items-center gap-3 rounded-lg border border-zinc-800 bg-zinc-900/40 px-4 py-3">
          <HardDrive size={18} className="text-zinc-400 shrink-0" />
          <div className="flex flex-col">
            <span data-testid="storage-total" className="text-sm text-zinc-200">
              Taitora canvases: {formatBytes(total)}
            </span>
            <span className={THEME.mcpPage.hint}>
              {entries.length === 0
                ? "No canvases stored yet."
                : `${entries.length} of the most recent forges, kept on this device.`}
            </span>
          </div>
        </div>

        {entries.length > 0 && (
          <ul className="flex flex-col gap-2">
            {entries.map((e) => (
              <li
                key={e.forgeId}
                data-testid="storage-row"
                className="flex items-center justify-between gap-4 rounded-lg border border-zinc-800 px-4 py-3"
              >
                <span className="font-mono text-xs text-zinc-400 truncate">{e.forgeId}</span>
                <span className="flex items-center gap-3 shrink-0">
                  {e.unsaved && (
                    <span className="text-[11px] uppercase tracking-wide text-amber-500">
                      Unsaved edits
                    </span>
                  )}
                  <span className="text-sm text-zinc-300 tabular-nums">{formatBytes(e.bytes)}</span>
                </span>
              </li>
            ))}
          </ul>
        )}

        <p className={THEME.mcpPage.hint}>
          Browsers cap storage at roughly 5&nbsp;MB per site, shared with your
          sidebar and toolbar preferences — so this figure is what Taitora
          canvases use, not how much room is left.
        </p>
      </div>
    </div>
  );
}
